import React, { useEffect, useRef, useState } from 'react';
import { Alert, Badge, Button, Col, Form, Row } from 'react-bootstrap';
import InstitutionLogo from '@/components/common/InstitutionLogo';
import LoadingSpinner from '@/components/common/LoadingSpinner';
import {
    updateCachedInstitution,
    useInstitutionDirectory,
} from '@/shared/hooks/useInstitutionPolicy';
import institutionService from '@/shared/services/institutionService';
import { useAuth } from '@/shared/contexts/AuthContext';
import { coordinatedInstitution } from '@/shared/utils/roles';
import { renderMarkdown } from '@/shared/utils/markdown';
import { serverMessage } from '@/shared/utils/serverMessage';
import './InstitutionsTab.css';

// Mirror the server's validation (institution.index.js). The server is the
// authority; checking here keeps a coordinator from losing a long edit to a
// round trip that fails on a length.
const MAX_EMAILS = 5;
const TEXT_MAX = 4000;
const LABEL_MAX = 120;
const THRESHOLD_MAX = 1000000;

// The policy fields in the order a member meets them. `link` marks the fields
// that take one optional https link; the rest are text only.
const FIELDS = [
    {
        name: 'capitalEquipment',
        title: 'Capital equipment',
        link: true,
        help: 'Shown on listings marked as capital equipment: the form or office that records a transfer.',
    },
    {
        name: 'assetTags',
        title: 'Asset tags',
        link: false,
        help: 'What a department does with an asset tag when an item changes hands.',
    },
    {
        name: 'dataDevices',
        title: 'Data-bearing devices',
        link: true,
        help: 'Why devices that can store data cannot be listed, and who to contact instead.',
    },
    {
        name: 'technologyDisposal',
        title: 'Technology disposal',
        link: true,
        help: 'Where computers and other electronics go instead of the exchange.',
    },
    {
        name: 'movingAndFacilities',
        title: 'Moving and facilities requests',
        link: false,
        help: 'How a move is arranged, since the exchange moves nothing.',
    },
    {
        name: 'acceptanceRules',
        title: 'Accepting a transfer',
        link: false,
        help: 'What both departments do once an owner accepts a request.',
    },
    {
        name: 'unclaimedItems',
        title: 'Unclaimed items',
        link: true,
        help: 'What to do with an item nobody claims.',
    },
];

function fieldDraft(field, value) {
    return {
        text: value.text,
        linkLabel: field.link && value.link ? value.link.label : '',
        linkUrl: field.link && value.link ? value.link.url : '',
    };
}

/** The editable state of one institution, as the server last reported it. */
function draftFromRow(row) {
    return {
        emails: [...row.facilitiesEmails],
        threshold: String(row.policy.capitalThresholdUsd),
        fields: Object.fromEntries(
            FIELDS.map((field) => [
                field.name,
                fieldDraft(field, row.policy[field.name]),
            ])
        ),
    };
}

const sameField = (a, b) =>
    a.text === b.text && a.linkLabel === b.linkLabel && a.linkUrl === b.linkUrl;

/**
 * The PUT body for what changed since the last server state, or the sentence
 * that says why it cannot be sent. Only changed fields are sent: the route
 * merges a partial policy, so an untouched field keeps what it holds and two
 * coordinators editing different fields do not overwrite each other.
 */
function buildPayload(row, draft) {
    const baseline = draftFromRow(row);
    const payload = {};

    const emails = draft.emails.map((email) => email.trim()).filter(Boolean);
    if (emails.join('\n') !== baseline.emails.join('\n')) {
        payload.facilitiesEmails = emails;
    }

    const policy = {};
    if (draft.threshold !== baseline.threshold) {
        const threshold = Number(draft.threshold);
        if (
            draft.threshold.trim() === '' ||
            !Number.isFinite(threshold) ||
            threshold < 0 ||
            threshold > THRESHOLD_MAX
        ) {
            return {
                error: `Enter a capital equipment threshold between 0 and ${THRESHOLD_MAX.toLocaleString()}.`,
            };
        }
        policy.capitalThresholdUsd = threshold;
    }
    for (const field of FIELDS) {
        const current = draft.fields[field.name];
        if (sameField(current, baseline.fields[field.name])) continue;
        const text = current.text.trim();
        if (!text) {
            return {
                error: `${field.title}: enter the text, or use Reset to default.`,
            };
        }
        if (!field.link) {
            policy[field.name] = { text };
            continue;
        }
        const label = current.linkLabel.trim();
        const url = current.linkUrl.trim();
        if (Boolean(label) !== Boolean(url)) {
            return {
                error: `${field.title}: enter both a link label and an https URL, or clear both.`,
            };
        }
        if (url && !/^https:\/\/\S+$/.test(url)) {
            return {
                error: `${field.title}: the link must start with https://.`,
            };
        }
        policy[field.name] = { text, link: url ? { label, url } : null };
    }
    if (Object.keys(policy).length) payload.policy = policy;
    return { payload };
}

const STATUS_CLASS = {
    success: 'text-success',
    danger: 'text-danger',
    info: 'text-body',
};

function Customization({ customized, noun }) {
    return customized ? (
        <Badge bg="dark">Customized</Badge>
    ) : (
        <Badge bg="secondary">Using the default {noun}</Badge>
    );
}

function InstitutionsTab() {
    const { userProfile } = useAuth();
    // A coordinator edits their own institution alone, so they get no picker
    // and the panel is fixed on it. The server refuses any other key.
    const scoped = coordinatedInstitution(userProfile);
    const { institutions, loading, error } = useInstitutionDirectory();
    const [active, setActive] = useState('');
    const [drafts, setDrafts] = useState({});
    const [status, setStatus] = useState({});
    const [busy, setBusy] = useState(false);
    const [focusEmail, setFocusEmail] = useState(-1);
    const emailRefs = useRef([]);
    const addRef = useRef(null);

    useEffect(() => {
        if (focusEmail >= 0 && emailRefs.current[focusEmail]) {
            emailRefs.current[focusEmail].focus();
            setFocusEmail(-1);
        }
    }, [focusEmail]);

    if (loading) {
        return <LoadingSpinner message="Loading institutions" />;
    }

    if (error || !institutions) {
        return (
            <Alert variant="danger">
                Could not load the campus directory. Check your connection and
                try again.
            </Alert>
        );
    }

    const row =
        institutions.find((i) => i.key === (scoped || active)) ||
        institutions[0];
    const key = row.key;
    const draft = drafts[key] || draftFromRow(row);
    const message = status[key];

    const patchDraft = (change) => {
        setStatus((previous) => ({ ...previous, [key]: null }));
        setDrafts((previous) => ({
            ...previous,
            [key]: { ...(previous[key] || draftFromRow(row)), ...change },
        }));
    };
    const patchField = (name, change) =>
        patchDraft({
            fields: {
                ...draft.fields,
                [name]: { ...draft.fields[name], ...change },
            },
        });

    const settle = (updated, text, keepDraft) => {
        updateCachedInstitution(updated);
        setDrafts((previous) => ({
            ...previous,
            [key]: keepDraft
                ? keepDraft(draftFromRow(updated))
                : draftFromRow(updated),
        }));
        setStatus((previous) => ({
            ...previous,
            [key]: { type: 'success', text },
        }));
    };
    const fail = (caught, fallback) =>
        setStatus((previous) => ({
            ...previous,
            [key]: { type: 'danger', text: serverMessage(caught, fallback) },
        }));

    const handleSave = async (event) => {
        event.preventDefault();
        const built = buildPayload(row, draft);
        if (built.error) {
            setStatus((previous) => ({
                ...previous,
                [key]: { type: 'danger', text: built.error },
            }));
            return;
        }
        if (Object.keys(built.payload).length === 0) {
            setStatus((previous) => ({
                ...previous,
                [key]: { type: 'info', text: 'There are no changes to save.' },
            }));
            return;
        }
        setBusy(true);
        setStatus((previous) => ({
            ...previous,
            [key]: { type: 'info', text: 'Saving.' },
        }));
        try {
            const updated = await institutionService.update(key, built.payload);
            settle(updated, `Saved the ${row.shortName} settings.`);
        } catch (caught) {
            fail(caught, 'Could not save these settings. Try again.');
        } finally {
            setBusy(false);
        }
    };

    // A reset takes effect at once rather than waiting for Save: it is a
    // statement about one field, and routing it through the form would make
    // "restore the default" depend on unrelated edits being valid.
    const handleReset = (name, title, apply) => async () => {
        setBusy(true);
        try {
            const updated = await institutionService.update(key, {
                policy: { [name]: null },
            });
            settle(updated, `Reset ${title} to the default.`, (fresh) => ({
                ...draft,
                ...apply(fresh),
            }));
        } catch (caught) {
            fail(caught, `Could not reset ${title}. Try again.`);
        } finally {
            setBusy(false);
        }
    };

    // Whether the form holds edits Save would send. Computed from the same
    // payload builder Save uses, so the note and the save never disagree.
    const pending = buildPayload(row, draft);
    const dirty = Boolean(
        pending.payload && Object.keys(pending.payload).length > 0
    );

    const setEmail = (index, value) =>
        patchDraft({
            emails: draft.emails.map((email, i) =>
                i === index ? value : email
            ),
        });

    return (
        <>
            <h2 className="h4 mb-3">Institutions</h2>
            <p className="text-muted">
                Each institution has facilities contacts and the policy text its
                members read. Where a field says it is using the default, the
                text is the researched starting point and nobody at that
                institution has confirmed or changed it.
            </p>
            {!scoped && (
                <Form.Group controlId="institution-picker" className="mb-4">
                    <Form.Label>Institution to edit</Form.Label>
                    <Form.Select
                        value={key}
                        onChange={(event) => setActive(event.target.value)}
                    >
                        {institutions.map((option) => (
                            <option key={option.key} value={option.key}>
                                {option.name}
                            </option>
                        ))}
                    </Form.Select>
                </Form.Group>
            )}

            <section aria-labelledby={`institution-${key}`}>
                <h3 id={`institution-${key}`} className="h5">
                    <InstitutionLogo institution={key} decorative /> {row.name}
                </h3>
                <p className="text-muted small">
                    Email domains: {row.domains.join(', ')}
                </p>
                {/* The editor is long, so it opens with a way to each part
                    of it; the headings it points at are the sections' own. */}
                <nav
                    aria-label={`${row.shortName} settings sections`}
                    className="mb-4"
                >
                    <ul className="afe-jump-links list-unstyled d-flex flex-wrap gap-2 mb-0">
                        <li>
                            <a href={`#${key}-contacts`}>Facilities contacts</a>
                        </li>
                        <li>
                            <a href={`#${key}-threshold`}>
                                Capital equipment threshold
                            </a>
                        </li>
                        {FIELDS.map((field) => (
                            <li key={field.name}>
                                <a href={`#${key}-${field.name}-title`}>
                                    {field.title}
                                </a>
                            </li>
                        ))}
                    </ul>
                </nav>
                <Form onSubmit={handleSave} noValidate>
                    <section
                        aria-labelledby={`${key}-contacts`}
                        className="mb-4 pb-3 border-bottom"
                    >
                        <h4 id={`${key}-contacts`} className="h6">
                            Facilities contacts
                        </h4>
                        <p
                            className="text-muted small"
                            id={`${key}-contacts-help`}
                        >
                            When an owner accepts a request, every address here
                            is copied on the acceptance email if this
                            institution services the building or the requester
                            belongs to it. With none, nobody is copied. Up to{' '}
                            {MAX_EMAILS} addresses.
                        </p>
                        {draft.emails.map((email, index) => (
                            <Row
                                key={index}
                                className="g-2 align-items-end mb-2"
                            >
                                <Col xs={12} sm>
                                    <Form.Group
                                        controlId={`${key}-email-${index}`}
                                    >
                                        <Form.Label>
                                            Facilities email {index + 1}
                                        </Form.Label>
                                        <Form.Control
                                            type="email"
                                            autoComplete="off"
                                            ref={(node) => {
                                                emailRefs.current[index] = node;
                                            }}
                                            value={email}
                                            aria-describedby={`${key}-contacts-help`}
                                            onChange={(event) =>
                                                setEmail(
                                                    index,
                                                    event.target.value
                                                )
                                            }
                                        />
                                    </Form.Group>
                                </Col>
                                <Col xs="auto">
                                    <Button
                                        variant="outline-secondary"
                                        onClick={() => {
                                            patchDraft({
                                                emails: draft.emails.filter(
                                                    (_, i) => i !== index
                                                ),
                                            });
                                            addRef.current?.focus();
                                        }}
                                    >
                                        Remove
                                        <span className="visually-hidden">
                                            {' '}
                                            facilities email {index + 1}
                                        </span>
                                    </Button>
                                </Col>
                            </Row>
                        ))}
                        {draft.emails.length === 0 && (
                            <p className="mb-2">
                                No facilities address is set for {row.shortName}
                                .
                            </p>
                        )}
                        <Button
                            ref={addRef}
                            variant="outline-primary"
                            disabled={draft.emails.length >= MAX_EMAILS}
                            onClick={() => {
                                patchDraft({ emails: [...draft.emails, ''] });
                                setFocusEmail(draft.emails.length);
                            }}
                        >
                            Add a facilities email
                        </Button>
                        {draft.emails.length >= MAX_EMAILS && (
                            <span className="text-muted small ms-2">
                                {MAX_EMAILS} is the most.
                            </span>
                        )}
                    </section>

                    <section
                        aria-labelledby={`${key}-threshold`}
                        className="mb-4 pb-3 border-bottom"
                    >
                        <div className="d-flex flex-wrap align-items-center gap-2 mb-2">
                            <h4 id={`${key}-threshold`} className="h6 mb-0">
                                Capital equipment threshold
                            </h4>
                            <Customization
                                customized={
                                    row.policyCustomized.capitalThresholdUsd
                                }
                                noun="value"
                            />
                        </div>
                        <Form.Group controlId={`${key}-threshold-input`}>
                            <Form.Label>Threshold in US dollars</Form.Label>
                            <Form.Control
                                type="number"
                                min={0}
                                max={THRESHOLD_MAX}
                                step={1}
                                inputMode="numeric"
                                value={draft.threshold}
                                aria-describedby={`${key}-threshold-help`}
                                onChange={(event) =>
                                    patchDraft({
                                        threshold: event.target.value,
                                    })
                                }
                            />
                            <Form.Text id={`${key}-threshold-help`} muted>
                                An item priced at or above this checks the
                                capital equipment box on the listing form.
                            </Form.Text>
                        </Form.Group>
                        <Button
                            variant="outline-secondary"
                            size="sm"
                            className="mt-2"
                            disabled={
                                busy ||
                                !row.policyCustomized.capitalThresholdUsd
                            }
                            onClick={handleReset(
                                'capitalThresholdUsd',
                                'the capital equipment threshold',
                                (fresh) => ({ threshold: fresh.threshold })
                            )}
                        >
                            Reset to default
                            <span className="visually-hidden">
                                {' '}
                                capital equipment threshold
                            </span>
                        </Button>
                    </section>

                    {FIELDS.map((field) => {
                        const value = draft.fields[field.name];
                        const base = `${key}-${field.name}`;
                        const preview = renderMarkdown(value.text);
                        const previewLinked =
                            field.link &&
                            value.linkLabel.trim() &&
                            /^https:\/\/\S+$/.test(value.linkUrl.trim());
                        return (
                            <section
                                key={field.name}
                                aria-labelledby={`${base}-title`}
                                className="mb-4 pb-3 border-bottom"
                            >
                                <div className="d-flex flex-wrap align-items-center gap-2 mb-1">
                                    <h4
                                        id={`${base}-title`}
                                        className="h6 mb-0"
                                    >
                                        {field.title}
                                    </h4>
                                    <Customization
                                        customized={
                                            row.policyCustomized[field.name]
                                        }
                                        noun="text"
                                    />
                                </div>
                                <p
                                    className="text-muted small"
                                    id={`${base}-help`}
                                >
                                    {field.help} Markdown, up to {TEXT_MAX}{' '}
                                    characters.
                                </p>
                                <Row className="g-3">
                                    <Col xs={12} lg={6}>
                                        <Form.Group controlId={`${base}-text`}>
                                            <Form.Label>
                                                {field.title} text
                                            </Form.Label>
                                            <Form.Control
                                                as="textarea"
                                                rows={6}
                                                maxLength={TEXT_MAX}
                                                value={value.text}
                                                aria-describedby={`${base}-help`}
                                                onChange={(event) =>
                                                    patchField(field.name, {
                                                        text: event.target
                                                            .value,
                                                    })
                                                }
                                            />
                                        </Form.Group>
                                        {field.link && (
                                            <Row className="g-2 mt-1">
                                                <Col xs={12} sm={5}>
                                                    <Form.Group
                                                        controlId={`${base}-label`}
                                                    >
                                                        <Form.Label>
                                                            {field.title} link
                                                            label
                                                        </Form.Label>
                                                        <Form.Control
                                                            type="text"
                                                            maxLength={
                                                                LABEL_MAX
                                                            }
                                                            value={
                                                                value.linkLabel
                                                            }
                                                            onChange={(event) =>
                                                                patchField(
                                                                    field.name,
                                                                    {
                                                                        linkLabel:
                                                                            event
                                                                                .target
                                                                                .value,
                                                                    }
                                                                )
                                                            }
                                                        />
                                                    </Form.Group>
                                                </Col>
                                                <Col xs={12} sm={7}>
                                                    <Form.Group
                                                        controlId={`${base}-url`}
                                                    >
                                                        <Form.Label>
                                                            {field.title} link
                                                            URL (https)
                                                        </Form.Label>
                                                        <Form.Control
                                                            type="url"
                                                            autoComplete="off"
                                                            placeholder="https://"
                                                            value={
                                                                value.linkUrl
                                                            }
                                                            onChange={(event) =>
                                                                patchField(
                                                                    field.name,
                                                                    {
                                                                        linkUrl:
                                                                            event
                                                                                .target
                                                                                .value,
                                                                    }
                                                                )
                                                            }
                                                        />
                                                    </Form.Group>
                                                </Col>
                                            </Row>
                                        )}
                                    </Col>
                                    <Col xs={12} lg={6}>
                                        <h5
                                            id={`${base}-preview`}
                                            className="h6"
                                        >
                                            Preview
                                        </h5>
                                        <div
                                            className="border rounded p-3"
                                            aria-labelledby={`${base}-preview`}
                                            role="group"
                                        >
                                            <div
                                                dangerouslySetInnerHTML={{
                                                    __html: preview,
                                                }}
                                            />
                                            {previewLinked && (
                                                <a
                                                    href={value.linkUrl.trim()}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                >
                                                    {value.linkLabel.trim()}
                                                </a>
                                            )}
                                        </div>
                                    </Col>
                                </Row>
                                <Button
                                    variant="outline-secondary"
                                    size="sm"
                                    className="mt-2"
                                    disabled={
                                        busy ||
                                        !row.policyCustomized[field.name]
                                    }
                                    onClick={handleReset(
                                        field.name,
                                        field.title,
                                        (fresh) => ({
                                            fields: {
                                                ...draft.fields,
                                                [field.name]:
                                                    fresh.fields[field.name],
                                            },
                                        })
                                    )}
                                >
                                    Reset to default
                                    <span className="visually-hidden">
                                        {' '}
                                        {field.title}
                                    </span>
                                </Button>
                            </section>
                        );
                    })}

                    {/* Sticks to the bottom of the screen so Save is in reach
                        from any section of the long form. */}
                    <div className="afe-save-bar d-flex flex-wrap align-items-center gap-3">
                        <Button type="submit" disabled={busy}>
                            Save
                            <span className="visually-hidden">
                                {' '}
                                the {row.shortName} settings
                            </span>
                        </Button>
                        <div
                            role="status"
                            className={
                                STATUS_CLASS[message ? message.type : 'info']
                            }
                        >
                            {message
                                ? message.text
                                : dirty
                                  ? 'You have unsaved changes.'
                                  : ''}
                        </div>
                    </div>
                </Form>
            </section>
        </>
    );
}

export default InstitutionsTab;
